import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaCalendarAlt,
  FaHospital,
  FaBox,
  FaWallet,
  FaUsers,
  FaTrash,
  FaBuilding,
  FaVial,
  FaSearch,
  FaStore,
  FaCalendarCheck,
  FaClock,
  FaEnvelope
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://api.elthiumhealth.com/api/admin";

const AdminBulkDiagnosticBooking = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDiagnostic, setSelectedDiagnostic] = useState(null);

  const [allStaff, setAllStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaffs, setSelectedStaffs] = useState([]);

  // Per-user booking details: map staffId -> { date, timeSlot }
  const [staffBookingDetails, setStaffBookingDetails] = useState({});

  const [ageFilter, setAgeFilter] = useState("");
  const [availableAges, setAvailableAges] = useState([]);

  const [diagnosticPackages, setDiagnosticPackages] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [slots, setSlots] = useState({
    homeCollectionSlots: [],
    centerVisitSlots: []
  });

  const [serviceType, setServiceType] = useState("Center Visit");
  const [globalNotes, setGlobalNotes] = useState("");
  const [sendEmails, setSendEmails] = useState(false);

  const [loading, setLoading] = useState({
    companies: false,
    diagnosticDetails: false,
    booking: false
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      const staffList = selectedCompany.staff || [];
      const uniqueStaff = [];
      const seenIds = new Set();
      for (const staff of staffList) {
        if (!seenIds.has(staff._id)) {
          seenIds.add(staff._id);
          uniqueStaff.push(staff);
        }
      }
      setAllStaff(uniqueStaff);
      resetState();
    } else {
      setAllStaff([]);
      setFilteredStaff([]);
      setAvailableAges([]);
      setSelectedBranch(null);
      setSelectedDiagnostic(null);
      setSelectedStaffs([]);
      setSelectedItems([]);
      setStaffBookingDetails({});
      setAgeFilter("");
      setDiagnosticPackages([]);
      setSlots({ homeCollectionSlots: [], centerVisitSlots: [] });
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      setFilteredStaff([]);
      setAvailableAges([]);
      return;
    }
    let filtered = [...allStaff];
    if (selectedBranch) {
      filtered = filtered.filter(staff => staff.branch === selectedBranch._id);
    }
    if (ageFilter && ageFilter !== "") {
      const targetAge = parseInt(ageFilter);
      if (!isNaN(targetAge)) {
        filtered = filtered.filter(staff => staff.age === targetAge);
      }
    }
    setFilteredStaff(filtered);

    let branchStaff = [...allStaff];
    if (selectedBranch) {
      branchStaff = branchStaff.filter(staff => staff.branch === selectedBranch._id);
    }
    const ages = [...new Set(branchStaff.map(s => s.age).filter(age => age != null))].sort((a, b) => a - b);
    setAvailableAges(ages);
  }, [allStaff, selectedBranch, ageFilter, selectedCompany]);

  useEffect(() => {
    if (selectedDiagnostic) {
      fetchDiagnosticDetails(selectedDiagnostic._id);
    } else {
      setDiagnosticPackages([]);
      setSlots({ homeCollectionSlots: [], centerVisitSlots: [] });
    }
  }, [selectedDiagnostic]);

  const fetchCompanies = async () => {
    try {
      setLoading((prev) => ({ ...prev, companies: true }));

      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${API_BASE}/companies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      console.error(
        "Error fetching companies:",
        error.response?.data || error.message
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load companies list",
      });
    } finally {
      setLoading((prev) => ({ ...prev, companies: false }));
    }
  };

  const fetchDiagnosticDetails = async (diagnosticId) => {
    try {
      setLoading((prev) => ({ ...prev, diagnosticDetails: true }));

      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${API_BASE}/diagnostics`,
        { id: diagnosticId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.diagnostic) {
        const diag = response.data.diagnostic;

        const packages = (diag.packages || []).map((pkg) => ({
          _id: pkg.packageId || pkg._id,
          name: pkg.packageDetails?.name || pkg.name,
          price: pkg.packageDetails?.price || pkg.price,
          description: pkg.packageDetails?.description || "",
        }));

        setDiagnosticPackages(packages);

        setSlots({
          homeCollectionSlots: diag.homeCollectionSlots || [],
          centerVisitSlots: diag.centerVisitSlots || [],
        });
      }
    } catch (error) {
      console.error(
        "Error fetching diagnostic details:",
        error.response?.data || error.message
      );

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load diagnostic packages and slots",
      });
    } finally {
      setLoading((prev) => ({ ...prev, diagnosticDetails: false }));
    }
  };

  const resetState = () => {
    setSelectedBranch(null);
    setSelectedDiagnostic(null);
    setSelectedStaffs([]);
    setSelectedItems([]);
    setStaffBookingDetails({});
    setAgeFilter("");
    setDiagnosticPackages([]);
    setSlots({ homeCollectionSlots: [], centerVisitSlots: [] });
  };

  const getWalletBalance = (staff) => {
    if (staff.wallet_balance && staff.wallet_balance > 0) return staff.wallet_balance;
    if (staff.forPackages && staff.forPackages > 0) return staff.forPackages;
    if (staff.totalAmount && staff.totalAmount > 0) return staff.totalAmount;
    return 0;
  };

  // Update selectedFor of all existing packages when user list changes
  const updateAllPackagesSelectedFor = (newStaffIds) => {
    setSelectedItems(prevItems =>
      prevItems.map(item => ({
        ...item,
        selectedFor: newStaffIds
      }))
    );
  };

  const addStaffToBulkList = (staffId) => {
    const staff = filteredStaff.find(s => s._id === staffId);
    if (!staff) return;
    if (selectedStaffs.some(s => s.staffId === staffId)) {
      Swal.fire({ toast: true, icon: "warning", title: "Already Added", timer: 2000 });
      return;
    }
    const wallet = getWalletBalance(staff);
    const newStaff = {
      staffId: staff._id,
      staffName: staff.name,
      employeeId: staff.employeeId || staff._id.slice(-4),
      department: staff.department || "N/A",
      walletBalance: wallet,
      gender: staff.gender || "Male",
      age: staff.age,
      contactNumber: staff.contact_number || "N/A",
      address: staff.address || "Not Provided"
    };

    const newSelectedStaffs = [...selectedStaffs, newStaff];
    setSelectedStaffs(newSelectedStaffs);

    // Update all existing packages to include the new staff in selectedFor
    setSelectedItems(prevItems =>
      prevItems.map(item => ({
        ...item,
        selectedFor: [...item.selectedFor, staff._id]
      }))
    );

    // Initialize per-user booking details
    setStaffBookingDetails(prev => ({
      ...prev,
      [staff._id]: { date: "", timeSlot: "" }
    }));
  };

  const removeStaffFromBulkList = (staffId) => {
    setSelectedStaffs(prev => prev.filter(staff => staff.staffId !== staffId));
    // Remove this staff from all packages' selectedFor
    setSelectedItems(prevItems =>
      prevItems.map(item => ({
        ...item,
        selectedFor: item.selectedFor.filter(id => id !== staffId)
      }))
    );
    setStaffBookingDetails(prev => {
      const newDetails = { ...prev };
      delete newDetails[staffId];
      return newDetails;
    });
  };

  const addAllFilteredStaff = () => {
    filteredStaff.forEach(staff => {
      if (!selectedStaffs.some(s => s.staffId === staff._id)) {
        addStaffToBulkList(staff._id);
      }
    });
  };

  const updateStaffDate = (staffId, date) => {
    setStaffBookingDetails(prev => ({
      ...prev,
      [staffId]: { ...prev[staffId], date, timeSlot: "" }
    }));
  };

  const updateStaffTimeSlot = (staffId, timeSlot) => {
    setStaffBookingDetails(prev => ({
      ...prev,
      [staffId]: { ...prev[staffId], timeSlot }
    }));
  };

  const getAvailableSlotsForStaff = () => {
    if (serviceType === "Home Collection") {
      return slots.homeCollectionSlots;
    } else {
      return slots.centerVisitSlots;
    }
  };

  const getAvailableDates = () => {
    const availableSlots = getAvailableSlotsForStaff();
    const dates = [...new Set(availableSlots.map(slot => slot.date))];
    return dates.sort();
  };

  const getTimeSlotsForDate = (date) => {
    const availableSlots = getAvailableSlotsForStaff();
    return availableSlots
      .filter(slot => slot.date === date && !slot.isBooked)
      .map(slot => slot.timeSlot);
  };

  const getFilteredPackages = () => {
    let packages = [...diagnosticPackages];
    if (searchTerm) {
      packages = packages.filter(pkg =>
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return packages;
  };

  const addItemToSelection = (pkg) => {
    const existing = selectedItems.find(i => i._id === pkg._id);
    if (existing) {
      setSelectedItems(prev => prev.map(i => i._id === pkg._id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      // Automatically set selectedFor to all current staff IDs
      const allStaffIds = selectedStaffs.map(s => s.staffId);
      setSelectedItems(prev => [...prev, {
        _id: pkg._id,
        name: pkg.name,
        price: pkg.price,
        quantity: 1,
        selectedFor: allStaffIds
      }]);
    }
  };

  const removeItemFromSelection = (itemId) => {
    setSelectedItems(prev => prev.filter(item => item._id !== itemId));
  };

  const updateItemQuantity = (itemId, quantity) => {
    if (quantity < 1) { removeItemFromSelection(itemId); return; }
    setSelectedItems(prev => prev.map(item => item._id === itemId ? { ...item, quantity } : item));
  };

  const toggleStaffForItem = (itemId, staffId) => {
    setSelectedItems(prev => prev.map(item => {
      if (item._id === itemId) {
        const selectedFor = item.selectedFor || [];
        const newSelectedFor = selectedFor.includes(staffId)
          ? selectedFor.filter(id => id !== staffId)
          : [...selectedFor, staffId];
        return { ...item, selectedFor: newSelectedFor };
      }
      return item;
    }));
  };

  const selectAllStaffForItem = (itemId) => {
    setSelectedItems(prev => prev.map(item =>
      item._id === itemId ? { ...item, selectedFor: selectedStaffs.map(s => s.staffId) } : item
    ));
  };

  const getTotalAmount = () => {
    return selectedItems.reduce((total, item) => {
      const staffCount = (item.selectedFor?.length > 0) ? item.selectedFor.length : selectedStaffs.length;
      return total + (item.price * (item.quantity || 1) * staffCount);
    }, 0);
  };

  const getTotalWalletBalance = () => {
    return selectedStaffs.reduce((total, staff) => total + (staff.walletBalance || 0), 0);
  };

  const buildBookings = () => {
    const bookings = [];
    for (const staff of selectedStaffs) {
      const staffBooking = staffBookingDetails[staff.staffId];
      if (!staffBooking || !staffBooking.date || !staffBooking.timeSlot) {
        continue;
      }
      selectedItems.forEach(item => {
        const applicableStaff = (item.selectedFor?.length > 0)
          ? [staff].filter(s => item.selectedFor.includes(s.staffId))
          : [staff];
        applicableStaff.forEach(s => {
          for (let i = 0; i < (item.quantity || 1); i++) {
            bookings.push({
              staffId: s.staffId,
              familyMemberId: null,
              diagnosticId: selectedDiagnostic._id,
              companyId: selectedCompany._id,
              branchId: selectedBranch._id,
              itemId: item._id,
              itemType: "package",
              serviceType: serviceType,
              date: staffBooking.date,
              timeSlot: staffBooking.timeSlot,
              notes: globalNotes,
              amount: item.price
            });
          }
        });
      });
    }
    return bookings;
  };

  const handleBulkBooking = async () => {
    if (!selectedCompany) return Swal.fire({ icon: "warning", title: "Select Company first" });
    if (!selectedBranch) return Swal.fire({ icon: "warning", title: "Select Branch first" });
    if (selectedStaffs.length === 0) return Swal.fire({ icon: "warning", title: "Add at least one user" });
    if (!selectedDiagnostic) return Swal.fire({ icon: "warning", title: "Select Diagnostic center" });
    if (selectedItems.length === 0) return Swal.fire({ icon: "warning", title: "Select at least one package" });

    // Validate each user has date and time
    const missingStaff = selectedStaffs.filter(staff => {
      const details = staffBookingDetails[staff.staffId];
      return !details || !details.date || !details.timeSlot;
    });
    if (missingStaff.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Date/Time",
        html: `Please select date and time for: ${missingStaff.map(s => s.staffName).join(", ")}`
      });
      return;
    }

    const totalAmount = getTotalAmount();
    const totalWallet = getTotalWalletBalance();
    if (totalAmount > totalWallet) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Insufficient Wallet Balance",
        html: `<p>Total: ₹${totalAmount.toFixed(2)}<br>Wallet: ₹${totalWallet.toFixed(2)}<br>Deficiency: ₹${(totalAmount - totalWallet).toFixed(2)}</p>`,
        showCancelButton: true,
        confirmButtonText: "Continue"
      });
      if (!result.isConfirmed) return;
    }
    proceedWithBulkBooking();
  };

  const proceedWithBulkBooking = async () => {
    setLoading((prev) => ({ ...prev, booking: true }));

    try {
      const token = localStorage.getItem("authToken");
      const bookings = buildBookings();

      const confirmed = await Swal.fire({
        icon: "info",
        title: "Confirm Bulk Booking",
        html: `<div class="text-left">
        <p><strong>Booking Summary:</strong></p>
        <p>• Company: ${selectedCompany.name}</p>
        <p>• Branch: ${selectedBranch.branchName}</p>
        <p>• Diagnostic: ${selectedDiagnostic.name}</p>
        <p>• Users: ${selectedStaffs.length}</p>
        <p>• Packages: ${selectedItems.length}</p>
        <p>• Service: ${serviceType}</p>
        <p>• Total Bookings: ${bookings.length}</p>
        <p>• Total Amount: ₹${getTotalAmount().toFixed(2)}</p>
        <p>• ${sendEmails
            ? "✅ Emails will be sent to users"
            : "❌ Emails will NOT be sent"
          }</p>
      </div>`,
        showCancelButton: true,
        confirmButtonText: "Create Bookings",
      });

      if (!confirmed.isConfirmed) return;

      const response = await axios.post(
        `${API_BASE}/create-bulk-diagnostic-bookings`,
        {
          bookings,
          companyId: selectedCompany._id,
          branchId: selectedBranch._id,
          diagnosticId: selectedDiagnostic._id,
          totalAmount: getTotalAmount(),
          sendEmails,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.isSuccessfull === true) {
        const {
          createdCount,
          totalWalletUsed,
          totalOnlinePaymentUsed,
          emailsSent,
        } = response.data;

        let message = `<strong>${createdCount} booking(s) created successfully!</strong><br>`;
        message += `Total Amount: ₹${getTotalAmount().toFixed(2)}<br>`;
        message += `Wallet Used: ₹${totalWalletUsed.toFixed(2)}<br>`;

        if (totalOnlinePaymentUsed > 0) {
          message += `Online Payment: ₹${totalOnlinePaymentUsed.toFixed(2)}<br>`;
        }

        message +=
          emailsSent > 0
            ? `📧 ${emailsSent} confirmation email(s) sent.`
            : "📧 No emails sent.";

        Swal.fire({
          icon: "success",
          title: "Booking Successful!",
          html: message,
          confirmButtonText: "View Bookings",
          showCancelButton: true,
          cancelButtonText: "Close",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/diagnosticslist");
          } else {
            resetForm();
          }
        });
      } else {
        throw new Error(response.data.message || "Booking failed");
      }
    } catch (error) {
      console.error(
        "Bulk booking error:",
        error.response?.data || error.message
      );

      Swal.fire({
        icon: "error",
        title: "Booking Failed",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading((prev) => ({ ...prev, booking: false }));
    }
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setSelectedBranch(null);
    setSelectedDiagnostic(null);
    setSelectedStaffs([]);
    setSelectedItems([]);
    setStaffBookingDetails({});
    setAgeFilter("");
    setDiagnosticPackages([]);
    setSlots({ homeCollectionSlots: [], centerVisitSlots: [] });
    setServiceType("Center Visit");
    setGlobalNotes("");
    setSendEmails(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-8 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-white">Bulk Diagnostic Booking</h1>
                <p className="text-purple-100 mt-1">Company → Branch → User → Age → Diagnostic → Packages → Booking (per user date/time)</p>
              </div>
              <button onClick={() => navigate(-1)} className="px-4 py-2 bg-white/20 text-white rounded-lg">Back</button>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* LEFT COLUMN */}
              <div className="lg:col-span-1 space-y-6">
                {/* 1. Company */}
                <div className="bg-white border rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaBuilding className="text-blue-500" /> 1. Select Company</h3>
                  <select value={selectedCompany?._id || ""} onChange={(e) => {
                    const company = companies.find(c => c._id === e.target.value);
                    setSelectedCompany(company || null);
                  }} className="w-full p-3 border rounded-lg">
                    <option value="">Select a company</option>
                    {companies.map(c => <option key={c._id} value={c._id}>{c.name} - {c.city}</option>)}
                  </select>
                </div>

                {/* 2. Branch */}
                {selectedCompany?.branches?.length > 0 && (
                  <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaStore className="text-green-500" /> 2. Select Branch</h3>
                    <select value={selectedBranch?._id || ""} onChange={(e) => {
                      const branch = selectedCompany.branches.find(b => b._id === e.target.value);
                      setSelectedBranch(branch || null);
                      setSelectedDiagnostic(null);
                      setSelectedStaffs([]);
                      setSelectedItems([]);
                      setStaffBookingDetails({});
                      setAgeFilter("");
                    }} className="w-full p-3 border rounded-lg">
                      <option value="">Select a branch</option>
                      {selectedCompany.branches.map(b => <option key={b._id} value={b._id}>{b.branchName} ({b.branchCode})</option>)}
                    </select>
                  </div>
                )}

                {/* 3. User */}
                {selectedBranch && (
                  <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaUsers className="text-purple-500" /> 3. Select User</h3>
                    <div className="mb-4">
                      <select onChange={e => { if (e.target.value) addStaffToBulkList(e.target.value); e.target.value = ""; }} className="w-full p-2 border rounded-lg mb-2">
                        <option value="">Add user member...</option>
                        {filteredStaff.filter(s => !selectedStaffs.some(ss => ss.staffId === s._id)).map(s => {
                          const wallet = getWalletBalance(s);
                          return (
                            <option key={s._id} value={s._id}>
                              {s.name} (Age: {s.age}) - ₹{wallet}
                            </option>
                          );
                        })}
                      </select>
                      <button onClick={addAllFilteredStaff} className="w-full py-2 bg-green-600 text-white rounded-lg">Add All ({filteredStaff.length})</button>
                    </div>
                    {selectedStaffs.length === 0 ? <p className="text-center text-gray-500">No user selected</p> : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedStaffs.map(staff => (
                          <div key={staff.staffId} className="border rounded-lg p-2 flex justify-between items-center">
                            <div><span className="font-medium">{staff.staffName}</span><br /><span className="text-xs">Age: {staff.age} | Wallet: ₹{staff.walletBalance}</span></div>
                            <button onClick={() => removeStaffFromBulkList(staff.staffId)} className="text-red-500"><FaTrash /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 4. Age Filter */}
                {selectedBranch && availableAges.length > 0 && (
                  <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaCalendarAlt className="text-yellow-500" /> 4. Filter User by Age (Optional)</h3>
                    <select value={ageFilter} onChange={e => setAgeFilter(e.target.value)} className="w-full p-3 border rounded-lg">
                      <option value="">All Ages</option>
                      {availableAges.map(age => <option key={age} value={age}>{age} years</option>)}
                    </select>
                  </div>
                )}

                {/* 5. Diagnostic */}
                {selectedCompany?.diagnostics?.length > 0 && selectedStaffs.length > 0 && (
                  <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaHospital className="text-red-500" /> 5. Select Diagnostic Center</h3>
                    <select
                      value={selectedDiagnostic?._id || ""}
                      onChange={(e) => {
                        const diagnostic = selectedCompany.diagnostics.find(d => d._id === e.target.value);
                        setSelectedDiagnostic(diagnostic || null);
                        setSelectedItems([]);
                      }}
                      className="w-full p-3 border rounded-lg"
                    >
                      <option value="">Select diagnostic center</option>
                      {selectedCompany.diagnostics.map(d => <option key={d._id} value={d._id}>{d.name} - {d.city}</option>)}
                    </select>
                    {loading.diagnosticDetails && <p className="text-xs text-gray-500 mt-1">Loading packages and slots...</p>}
                  </div>
                )}

                {/* 6. Global Settings: Service Type & Notes */}
                {selectedDiagnostic && (
                  <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaCalendarAlt className="text-yellow-500" /> 6. Service Settings</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setServiceType("Home Collection")} className={`p-2 border rounded-lg ${serviceType === "Home Collection" ? "bg-blue-100" : "bg-gray-50"}`}>Home Collection</button>
                        <button onClick={() => setServiceType("Center Visit")} className={`p-2 border rounded-lg ${serviceType === "Center Visit" ? "bg-blue-100" : "bg-gray-50"}`}>Center Visit</button>
                      </div>
                      <textarea
                        rows="2"
                        value={globalNotes}
                        onChange={e => setGlobalNotes(e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="Global notes (applies to all bookings)"
                      />
                    </div>
                  </div>
                )}

                {/* Email Toggle */}
                <div className="bg-white border rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FaEnvelope className="text-blue-600 text-lg" />
                      <span className="text-sm font-medium text-gray-800">Send confirmation emails to user</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendEmails}
                        onChange={(e) => setSendEmails(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700">{sendEmails ? "Yes" : "No"}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - Packages & User Date/Time */}
              <div className="lg:col-span-2">
                <div className="bg-white border rounded-lg p-6 shadow-sm h-full flex flex-col">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><FaBox className="text-orange-500" /> 7. Select Packages & Assign Dates/Times to User</h3>
                  {selectedDiagnostic ? (
                    <>
                      {/* Package selection */}
                      <div className="mb-4">
                        <div className="relative">
                          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input type="text" placeholder="Search packages..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-3 py-2 border rounded-lg" />
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-[300px]">
                        {loading.diagnosticDetails ? (
                          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div><p className="mt-2">Loading packages...</p></div>
                        ) : getFilteredPackages().length === 0 ? (
                          <div className="text-center py-8 text-gray-500">No packages found</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {getFilteredPackages().map(pkg => (
                              <div key={pkg._id} className="border rounded-lg p-3 hover:bg-gray-50">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-800">PACKAGE</span><h4 className="font-medium">{pkg.name}</h4></div>
                                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">{pkg.description?.substring(0, 80)}</p>
                                    <div className="flex justify-between items-center"><span className="font-bold">₹{pkg.price}</span><button onClick={() => addItemToSelection(pkg)} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg">Add</button></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Selected Packages Summary */}
                      {selectedItems.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3">Selected Packages</h4>
                          <div className="space-y-3 max-h-60 overflow-y-auto">
                            {selectedItems.map(item => (
                              <div key={item._id} className="border rounded-lg p-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between"><span className="font-medium">{item.name}</span><button onClick={() => removeItemFromSelection(item._id)} className="text-red-500"><FaTrash /></button></div>
                                    <div className="flex items-center justify-between mt-2">
                                      <div className="flex items-center gap-2"><span className="text-sm">Qty:</span><div className="flex items-center border rounded"><button onClick={() => updateItemQuantity(item._id, (item.quantity || 1) - 1)} className="px-2 py-1">-</button><span className="px-3 py-1 border-x">{item.quantity || 1}</span><button onClick={() => updateItemQuantity(item._id, (item.quantity || 1) + 1)} className="px-2 py-1">+</button></div></div>
                                      <span className="font-bold">₹{(item.price * (item.quantity || 1)).toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                                {selectedStaffs.length > 0 && (
                                  <div className="mt-2">
                                    <div className="flex justify-between items-center mb-1"><span className="text-xs text-gray-700">Apply to user:</span><button onClick={() => selectAllStaffForItem(item._id)} className="text-xs text-blue-600">Select All</button></div>
                                    <div className="flex flex-wrap gap-1">{selectedStaffs.map(staff => (<button key={staff.staffId} onClick={() => toggleStaffForItem(item._id, staff.staffId)} className={`px-2 py-1 text-xs rounded ${item.selectedFor?.includes(staff.staffId) ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100'}`}>{staff.staffName.split(' ')[0]}</button>))}</div>
                                    {item.selectedFor?.length === 0 && selectedStaffs.length > 0 && (
                                      <p className="text-xs text-red-500 mt-1">No user selected. Booking will not be created for any user.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Per-user date/time selection */}
                      {selectedStaffs.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3">Assign Date & Time for Each User</h4>
                          {loading.diagnosticDetails ? (
                            <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div><p>Loading slots...</p></div>
                          ) : getAvailableSlotsForStaff().length === 0 ? (
                            <div className="text-center py-4 text-gray-500"><FaClock className="text-3xl text-gray-300 mx-auto mb-2" /><p>No slots available for {serviceType}</p></div>
                          ) : (
                            <div className="space-y-4 max-h-[400px] overflow-y-auto">
                              {selectedStaffs.map(staff => {
                                const staffDetail = staffBookingDetails[staff.staffId] || { date: "", timeSlot: "" };
                                const availableDates = getAvailableDates();
                                const timeSlots = staffDetail.date ? getTimeSlotsForDate(staffDetail.date) : [];
                                return (
                                  <div key={staff.staffId} className="border rounded-lg p-3 bg-gray-50">
                                    <div className="font-medium mb-2">{staff.staffName} (Age: {staff.age})</div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Select Date</label>
                                        <select
                                          value={staffDetail.date}
                                          onChange={(e) => updateStaffDate(staff.staffId, e.target.value)}
                                          className="w-full p-2 text-sm border rounded-lg"
                                        >
                                          <option value="">Choose date</option>
                                          {availableDates.map(date => (
                                            <option key={date} value={date}>
                                              {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Select Time Slot</label>
                                        <select
                                          value={staffDetail.timeSlot}
                                          onChange={(e) => updateStaffTimeSlot(staff.staffId, e.target.value)}
                                          className="w-full p-2 text-sm border rounded-lg"
                                          disabled={!staffDetail.date}
                                        >
                                          <option value="">Choose time</option>
                                          {timeSlots.map(ts => (
                                            <option key={ts} value={ts}>{ts}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                    {staffDetail.date && timeSlots.length === 0 && (
                                      <p className="text-xs text-red-500 mt-1">No available slots on this date</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Summary and Create Button */}
                      {selectedStaffs.length > 0 && selectedItems.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-gray-50 p-3 rounded-lg text-center"><div className="text-sm">Total Users</div><div className="text-2xl font-bold text-purple-600">{selectedStaffs.length}</div></div>
                            <div className="bg-gray-50 p-3 rounded-lg text-center"><div className="text-sm">Total Amount</div><div className="text-2xl font-bold text-green-600">₹{getTotalAmount().toFixed(2)}</div></div>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                            <div className="flex items-center gap-2 mb-2"><FaWallet className="text-yellow-600" /> Wallet Summary</div>
                            <div className="grid grid-cols-2 text-sm">Total Wallet: ₹{getTotalWalletBalance().toFixed(2)}<br />Total Amount: ₹{getTotalAmount().toFixed(2)}<br /><span className={`col-span-2 font-bold ${getTotalAmount() <= getTotalWalletBalance() ? 'text-green-600' : 'text-red-600'}`}>{getTotalAmount() <= getTotalWalletBalance() ? '✓ Sufficient Balance' : '✗ Insufficient'}</span></div>
                          </div>
                          <button onClick={handleBulkBooking} disabled={loading.booking} className={`w-full py-3 rounded-lg text-white font-medium ${loading.booking ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"}`}>{loading.booking ? "Creating..." : "Create Bulk Booking"}</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">Select a diagnostic center first to see packages</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBulkDiagnosticBooking;